import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import {
    Done,
    Close,
    PriorityHigh,
    PriorityHighRounded,
} from '@mui/icons-material';
import { usePather } from "react-pather";
import { AlertMessageLink } from '../advanced/LoginMessage';
import { RadioExtended } from '../../general';

const reservedSlugMode = 'customcode';

const CustomCode = ({ slugMode, setSlugMode, ...props }) => {
    return (
        <RadioExtended
            currentValue={slugMode}
            value={reservedSlugMode}
            setValue={setSlugMode}
            title='CustomCode'
            expandedBlock={
                <CustomCodeExpanded {...props}/>
            }
            radioName='radio-buttons-code-type' 
            hideTitleWhenExpanded={true}
        />
    );
}

const CustomCodeExpanded = props => {
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', rowGap: '10px'}}>
            <CustomCodeInput {...props}/>
            <CustomCodeDescription />
        </Box>
    );
}

const CustomCodeInput = ({ advanced, setAdvancedSafe, additional, loadingCustomCodeCheck, isCustomCodeInvalid, isPathOccupied }) => {
    return (
        <Box sx={{display: 'flex', alignItems: 'flex-end'}}>
            <CustomCodeInputField 
                advanced={advanced} 
                setAdvancedSafe={setAdvancedSafe} 
                additional={additional}
            />
            <CustomCodeAvailabilityChecker 
                additional={additional}
                loadingCustomCodeCheck={loadingCustomCodeCheck}
                isCustomCodeInvalid={isCustomCodeInvalid}
                isPathOccupied={isPathOccupied}
            />
        </Box>
    );
}

const CustomCodeInputField = ({ advanced, setAdvancedSafe }) => {
    return (
        <Box sx={{display: 'flex', alignItems: {xs: 'flex-start', sm: 'flex-end'}, flexDirection: {xs: 'column', sm: 'row'}, gap: {xs: 1}}}>
            <Box>
                <Typography component="div" sx={{fontWeight: 'bold'}}>
                    Custom code: 
                </Typography>
            </Box>
            <Box>
                <TextField
                    id="outlined-number-custom-code"
                    InputLabelProps={{ shrink: true }}
                    variant="standard" 
                    sx={{ ml: { sm: 1 } }}
                    value={advanced.customCode}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{fontSize: '14px'}}>
                                    {advanced.customCode.length}/100
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{ maxLength: 100 }}
                    onChange={e => setAdvancedSafe({ customCode: e.target.value })}
                />  
            </Box>
        </Box>
    );
}

const CustomCodeAvailabilityChecker = ({ additional, loadingCustomCodeCheck, isCustomCodeInvalid, isPathOccupied }) => {
    return (
        <Box sx={{display: 'flex', alignItems: 'center', ml: 1}}>
            <>
            {
                loadingCustomCodeCheck ?
                <CircularProgress size={18}/> :
                <>
                {
                    additional.isCustomCodeAvailable !== null ? 
                    <>
                        {
                            additional.isCustomCodeAvailable ?
                            <Tooltip title="Code is available" placement="right">
                                <Done fontSize="inherit" sx={{color: 'green'}} /> 
                            </Tooltip>:
                            <Tooltip title="Code is unavailable" placement="right">
                                <Close fontSize="inherit" sx={{color: 'red'}} />
                            </Tooltip>
                        }
                    </> :   
                    <>
                        {
                            isPathOccupied &&
                            <Tooltip title="Reserved" placement="right">
                                <Close fontSize="inherit" sx={{color: 'red'}} />
                            </Tooltip>
                        }
                        {   
                            !isPathOccupied && isCustomCodeInvalid && 
                            <Tooltip title="Code can contain only A-Za-z0-9-_ characters and must have a length between 5 and 100" placement="right">
                                <PriorityHigh fontSize="inherit" sx={{color: 'red'}} />
                            </Tooltip>
                        }
                    </>
                }
                </>
            }
            </>
        </Box>
    );
}

const CustomCodeDescription = () => {
    const pather = usePather();
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
            <Typography component="div" sx={{fontSize: '12px'}}>
                Custom code must be 5-100 characters long and can only contain letters, numbers, hyphens, and underscores.
            </Typography>
            <Box sx={{
                width: '100%', display: 'flex', alignItems: 'center', fontSize: '0.7rem', px: 2, py: 1.5,
                backgroundColor: '#E84545', color: 'white', borderRadius: '15px',
            }}>
                <Box>
                    <b>Custom codes between 5 and 10 characters (inclusive) can be valid for up to 100 days. </b>
                    After this period, the company reserves the right to reclaim the code and replace it with an automatically generated one (AutoCode), notifying the user by email (if the email is confirmed).
                    Custom codes longer than 10 characters are exempt from this limitation.
                    Additionally, codes flagged as <b>suspicious or potentially misleading</b> may be replaced at any time to maintain service integrity and user trust. 
                    For more information, please refer to our <AlertMessageLink text='Terms and Conditions' route={pather.front.termsConditions}/>.
                </Box>
                <Box sx={{marginLeft: 'auto'}}>
                    <PriorityHighRounded/>
                </Box>
            </Box>
        </Box>
    );
}

export {
    reservedSlugMode
}

export default CustomCode;